// Guarded localStorage access. localStorage throws rather than returning null
// when storage is unavailable (private browsing, blocked cookies, over quota),
// and those throws happen at call sites that cannot do anything useful with
// them, so every read and write in the shell goes through here.
//
// The same shape already exists in `database/utils/rowEditModalSidebar.js` and
// in `database/components/table/Table.vue`; this module is the shared version
// for state that has more than one reader.

/**
 * Returns the stored string for `key`, or `defaultValue` when the key is unset
 * or storage is unavailable.
 */
export function readLocalStorage(key, defaultValue = null) {
  try {
    const value = window.localStorage.getItem(key)
    return value === null ? defaultValue : value
  } catch (e) {
    // Storage can be unavailable (private mode, blocked cookies); the caller
    // must still work, just without remembering.
    return defaultValue
  }
}

/**
 * Reads a boolean that defaults to `false` when absent. Only the exact string
 * `'true'` is truthy: an absent key must never read as opted-in, because chrome
 * that takes space from the content is opt-in.
 */
export function readLocalStorageBoolean(key, defaultValue = false) {
  const value = readLocalStorage(key, null)
  if (value === null) {
    return defaultValue
  }
  return value === 'true'
}

/**
 * Stores `value` stringified under `key`. No-op when storage is unavailable.
 */
export function writeLocalStorage(key, value) {
  try {
    window.localStorage.setItem(key, String(value))
  } catch (e) {
    // Ignore storage errors (e.g. unavailable in private mode or over quota).
  }
}

// Shell geometry and openness, persisted per user (browser) rather than per
// workspace or table: these are preferences about the user's screen, not about
// the data they are looking at.
export const SHELL_LEFT_COLLAPSED_KEY = 'baserow.shell.leftCollapsed'
export const SHELL_LEFT_WIDTH_KEY = 'baserow.shell.leftWidth'
export const SHELL_RIGHT_OPEN_KEY = 'baserow.shell.rightOpen'
export const SHELL_RIGHT_WIDTH_KEY = 'baserow.shell.rightWidth'

// Which of the two left navigation surfaces the user last expanded --
// `'sidebar'`, `'views'`, or absent for neither. Model C
// (`10-placement-proposal.md` §4.3) lets at most one of them be expanded at a
// time, so on reload both surfaces would otherwise restore their own stored
// preference independently and both come back expanded, which is the exact
// state the model exists to prevent.
//
// This records *who won the budget*, never *what the user prefers*: the two
// surfaces keep their own collapse preferences, and this only breaks the tie
// between them. A forced collapse must not touch either.
export const SHELL_LEFT_NAV_EXPANDED_KEY = 'baserow.shell.leftNavExpanded'
